import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * useRecentOrders — Phase 2.2.
 *
 * Polls /api/orders/recent on a short interval. Returns the raw
 * shape from the API so the page can adapt it to whatever card
 * layout it wants. Polling instead of websockets for v1 — the KDS
 * (Phase 2.10+) will move to a real-time channel.
 *
 * Polling cadence:
 *   - 8s while tab is visible
 *   - paused while the tab is hidden (saves battery + Railway egress)
 *   - manual refresh exposed for after place-order
 */

export interface ApiRecentOrder {
  id: string;
  orderNumber: number;
  placedAt: string;
  totalCents: number;
  status: string;
  channel: 'DINE_IN' | 'TAKEAWAY' | 'DELIVERY';
  customerName: string | null;
  cashier: { id: string; name: string };
  lines: { name: string; qty: number }[];
}

const POLL_MS = 8_000;

// Stale-while-revalidate cache so a fresh tab load instantly renders the
// last-known recent orders instead of a flash of seed data.
const CACHE_KEY = 'codedpos.recentorders.v1';
function readCache(): ApiRecentOrder[] | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch { return null; }
}
function writeCache(orders: ApiRecentOrder[]) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(orders.slice(0, 20))); } catch { /* ignore */ }
}

export function useRecentOrders() {
  const initial = readCache();
  const [orders, setOrders] = useState<ApiRecentOrder[]>(initial ?? []);
  const [loading, setLoading] = useState(initial == null);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchOnce = useCallback(async () => {
    try {
      const r = await fetch('/api/orders/recent?limit=20', { credentials: 'include' });
      if (!r.ok) {
        if (r.status === 401) return; // not signed in yet
        throw new Error(`HTTP ${r.status}`);
      }
      const data = (await r.json()) as { orders: ApiRecentOrder[] };
      const next = data.orders ?? [];
      setOrders(next);
      writeCache(next);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOnce();
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        fetchOnce();
        if (!timer.current) timer.current = setInterval(fetchOnce, POLL_MS);
      } else if (timer.current) {
        clearInterval(timer.current);
        timer.current = null;
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    timer.current = setInterval(fetchOnce, POLL_MS);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      if (timer.current) clearInterval(timer.current);
      timer.current = null;
    };
  }, [fetchOnce]);

  return { orders, loading, error, refresh: fetchOnce };
}
